import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import PageHero from "../../components/motion/PageHero";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ProtectedVideoPlayer from "../../components/video/ProtectedVideoPlayer";
import AuthContext from "../../context/AuthContext";
import { useContentProtection } from "../../hooks/useContentProtection";
import { useConstants } from "../../hooks/UseConstants";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";

function parseFilename(disposition, fallback = 'lesson-file') {
    if (!disposition || typeof disposition !== 'string') return fallback;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            return utf8Match[1];
        }
    }

    const basicMatch = disposition.match(/filename="?([^"]+)"?/i);
    return basicMatch?.[1] || fallback;
}

function LessonViewer() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { courseId, fileId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [file, setFile] = useState(null);
    const [contentId, setContentId] = useState(null);
    const [progressMsg, setProgressMsg] = useState('');
    const [progressTone, setProgressTone] = useState('success');
    const [fileActionLoading, setFileActionLoading] = useState(false);
    const [videoSrc, setVideoSrc] = useState('');
    const [videoLoading, setVideoLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            const [fileRes, courseRes] = await Promise.all([
                Fetch(`${host}/courses/${courseId}/files/${fileId}/show`, 'GET'),
                Fetch(`${host}/courses/${courseId}/show`, 'GET'),
            ]);
            if (fileRes.status === 200) setFile(fileRes.data.data);
            if (courseRes.status === 200) {
                const contents = courseRes.data.data?.contents || [];
                const match = contents.find(
                    c => c.content_type !== 'Exam' && String(c.content?.id) === String(fileId)
                );
                if (match) setContentId(match.id);
            }
            setLoading(false);
        };
        load();
    }, [host, courseId, fileId]);

    useEffect(() => {
        const loadPlaybackTicket = async () => {
            if (!file?.is_video || !file?.playback_ticket_url) {
                setVideoSrc(file?.file_url || '');
                setVideoLoading(false);
                return;
            }

            setVideoSrc('');
            setVideoLoading(true);
            const result = await Fetch(file.playback_ticket_url, 'GET');
            if (result.status === 200) {
                setVideoSrc(result.data?.data?.url || '');
                setProgressMsg('');
            } else {
                setProgressTone('error');
                setProgressMsg(result.data?.errors?.[0] || result.data?.message || (isAr ? 'فشل تجهيز الفيديو المحمي' : 'Failed to prepare protected video'));
                setVideoSrc('');
            }
            setVideoLoading(false);
        };

        loadPlaybackTicket();
    }, [file, isAr]);

    const reloadPlaybackTicket = useCallback(async () => {
        if (!file?.playback_ticket_url) return;
        const result = await Fetch(file.playback_ticket_url, 'GET');
        if (result.status === 200) {
            setVideoSrc(result.data?.data?.url || '');
        }
    }, [file]);

    const isVideo = Boolean(file?.is_video);
    const protectionEnabled = isVideo && Boolean(videoSrc);

    useContentProtection({
        enabled: protectionEnabled,
        onViolation: reloadPlaybackTicket,
    });

    const markComplete = async () => {
        if (!contentId) return;
        const result = await Fetch(
            `${host}/student/courses/${courseId}/progress/content/${contentId}`,
            'POST',
            {}
        );
        if (result.status === 200) {
            setProgressTone('success');
            const progressPercent = result.data.data?.progress_percentage ?? 0;
            const certificateIssued = result.data.data?.certificate_issued;
            setProgressMsg(
                certificateIssued
                    ? (isAr
                        ? `تم تحديث التقدم: ${progressPercent}% والشهادة أصبحت متاحة الآن.`
                        : `Progress updated: ${progressPercent}% and your certificate is now available.`)
                    : (isAr
                        ? `تم تحديث التقدم: ${progressPercent}%`
                        : `Progress updated: ${progressPercent}%`)
            );
        }
    };

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress /></Box>;
    if (!file) return <Typography>{isAr ? 'الملف غير موجود' : 'File not found'}</Typography>;

    const protectedFileUrl = file.protected_file_url || file.file_url;
    const protectedDownloadUrl = file.protected_download_url || protectedFileUrl;
    const watermark = user?.email
        ? `${user.email} #${user.id}`
        : `Altus Academy #${localStorage.getItem('id') || 'student'}`;
    const lessonName = isAr ? (file.name_ar || file.name_en) : (file.name_en || file.name_ar);
    const accessLabel = isVideo
        ? (isAr ? 'فيديو محمي' : 'Protected video')
        : (isAr ? 'ملف محمي' : 'Protected file');
    const availabilityLabel = protectedFileUrl || videoSrc
        ? (isAr ? 'جاهز للوصول' : 'Ready to access')
        : (isAr ? 'غير متاح حالياً' : 'Currently unavailable');

    const openProtectedFile = async (download = false) => {
        const targetUrl = download ? protectedDownloadUrl : protectedFileUrl;
        if (!targetUrl) return;

        if (!isVideo) {
            const link = document.createElement('a');
            link.href = targetUrl;
            link.rel = 'noopener noreferrer';

            if (download) {
                link.download = file.name_en || file.name_ar || 'lesson-file';
            } else {
                link.target = '_blank';
            }

            document.body.appendChild(link);
            link.click();
            link.remove();
            return;
        }

        setFileActionLoading(true);
        try {
            const response = await fetch(targetUrl, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    Accept: '*/*',
                },
            });

            if (!response.ok) {
                throw new Error(isAr ? 'تعذر الوصول إلى الملف' : 'Unable to access file');
            }

            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const fallbackName = `${file.name_en || file.name_ar || 'lesson-file'}`;
            const filename = parseFilename(response.headers.get('content-disposition'), fallbackName);

            if (download) {
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
            } else {
                const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');
                if (!opened) {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }
            }

            window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
        } catch (error) {
            setProgressTone('error');
            setProgressMsg(error?.message || (isAr ? 'فشل تحميل الملف' : 'File access failed'));
        } finally {
            setFileActionLoading(false);
        }
    };

    return (
        <Box
            sx={{
                userSelect: isVideo ? 'none' : undefined,
                WebkitUserSelect: isVideo ? 'none' : undefined,
                WebkitTouchCallout: isVideo ? 'none' : undefined,
            }}
            onContextMenu={isVideo ? (event) => event.preventDefault() : undefined}
        >
            <PageHero
                title={lessonName}
                subtitle={isVideo
                    ? (isAr ? 'مشاهدة آمنة للمحتوى مع تتبع التقدم ومنع الوصول غير المصرح به.' : 'Secure lesson playback with progress tracking and protected access.')
                    : (isAr ? 'ملف تعليمي متاح للعرض أو التنزيل' : 'Learning file available to view or download')}
                icon={<PlayCircleOutlineIcon />}
                actions={
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate(`/courses/${courseId}`)}
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: 'white' } }}
                    >
                        {isAr ? 'العودة للدورة' : 'Back to course'}
                    </Button>
                }
            />

            {progressMsg ? (
                <Box
                    sx={{
                        mb: 3,
                        p: 1.75,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: progressTone === 'error' ? 'rgba(239,68,68,0.28)' : 'rgba(34,197,94,0.28)',
                        bgcolor: progressTone === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ color: progressTone === 'error' ? 'error.main' : 'success.main', fontWeight: 700 }}
                    >
                        {progressMsg}
                    </Typography>
                </Box>
            ) : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.6fr) minmax(320px, 0.8fr)' }, gap: 3 }}>
                <DashboardPanelCard
                    title={isVideo ? (isAr ? 'المشغل الآمن' : 'Secure player') : (isAr ? 'الوصول إلى الملف' : 'File access')}
                    subtitle={
                        isVideo
                            ? (isAr ? 'يتوقف الفيديو فوراً وتظهر شاشة سوداء عند لقطة الشاشة أو تسجيل الشاشة أو فقدان التركيز.' : 'Video stops immediately with a black screen on screenshot, screen recording, or focus loss.')
                            : (isAr ? 'افتح الملف للعرض أو نزّله مباشرة إلى جهازك.' : 'Open the file for preview or download it directly to your device.')
                    }
                    className="mx-0"
                >
                    {isVideo ? (
                        videoLoading ? (
                            <Box className="flex justify-center py-12"><CircularProgress /></Box>
                        ) : videoSrc ? (
                            <ProtectedVideoPlayer
                                src={videoSrc}
                                watermark={watermark}
                                onEnded={markComplete}
                                onSecurityViolation={reloadPlaybackTicket}
                                labels={{
                                    protectedTitle: isAr ? 'محتوى محمي' : 'Protected content',
                                    protectedSubtitle: isAr
                                        ? 'تم إيقاف التشغيل. عد إلى هذه النافذة ثم اضغط «متابعة المشاهدة».'
                                        : 'Playback stopped. Return to this window, then press Resume playback.',
                                    captureSubtitle: isAr
                                        ? 'لا يُسمح بلقطة الشاشة أو تسجيلها داخل صفحة الدرس. لن يظهر الفيديو حتى تضغط متابعة المشاهدة.'
                                        : 'Screenshots and screen recording are blocked on this lesson page. Video stays hidden until you resume.',
                                    resumeLabel: isAr ? 'متابعة المشاهدة' : 'Resume playback',
                                    cooldownLabel: isAr ? 'انتظر {seconds} ثانية قبل المتابعة' : 'Wait {seconds}s before resuming',
                                }}
                            />
                        ) : (
                            <Typography color="error.main">
                                {isAr ? 'تعذر تجهيز الفيديو حالياً.' : 'Could not prepare the video right now.'}
                            </Typography>
                        )
                    ) : protectedFileUrl ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <Button
                                variant="contained"
                                onClick={() => openProtectedFile(false)}
                                disabled={fileActionLoading}
                                sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}
                            >
                                {fileActionLoading ? <CircularProgress size={20} color="inherit" /> : (isAr ? 'فتح الملف' : 'Open file')}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadOutlinedIcon />}
                                onClick={() => openProtectedFile(true)}
                                disabled={fileActionLoading}
                            >
                                {isAr ? 'تنزيل الملف' : 'Download file'}
                            </Button>
                        </Box>
                    ) : (
                        <Typography color="text.secondary">{isAr ? 'لا يوجد رابط متاح للملف حالياً.' : 'No file URL is currently available.'}</Typography>
                    )}
                </DashboardPanelCard>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <DashboardPanelCard
                        title={isAr ? 'تفاصيل الدرس' : 'Lesson details'}
                        subtitle={isAr ? 'معلومات سريعة عن نوع الوصول وحالة التجهيز.' : 'Quick details about access type and availability.'}
                        className="mx-0"
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Chip icon={<ShieldOutlinedIcon />} label={accessLabel} color="primary" variant="outlined" />
                            <Chip
                                label={availabilityLabel}
                                color={(protectedFileUrl || videoSrc) ? 'success' : 'default'}
                                variant="outlined"
                            />
                            {contentId ? (
                                <Chip
                                    icon={<CheckCircleOutlineIcon />}
                                    label={isAr ? 'يرتبط بالتقدم' : 'Tracks progress'}
                                    color="success"
                                    variant="outlined"
                                />
                            ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {isVideo
                                ? (isAr
                                    ? 'تشغيل الفيديو يتم عبر تذكرة مؤقتة ومحمية مرتبطة بجلسة الطالب.'
                                    : 'Video playback uses a short-lived protected ticket tied to the student session.')
                                : (isAr
                                    ? 'الملف يفتح أو يُنزّل فقط بعد التحقق من هوية الطالب وصلاحيته.'
                                    : 'The file can only be opened or downloaded after validating the student session and access rights.')}
                        </Typography>
                    </DashboardPanelCard>

                    <DashboardPanelCard
                        title={isAr ? 'التقدم داخل الدورة' : 'Course progress'}
                        subtitle={isAr ? 'يمكنك تعليم الدرس كمكتمل يدوياً إذا لزم الأمر.' : 'You can manually mark the lesson as complete when needed.'}
                        className="mx-0"
                    >
                        {contentId ? (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={markComplete}
                                sx={{ mb: isVideo ? 2 : 0 }}
                            >
                                {isAr ? 'تعليم الدرس كمكتمل' : 'Mark lesson complete'}
                            </Button>
                        ) : (
                            <Typography color="text.secondary">
                                {isAr ? 'هذا العنصر غير مرتبط حالياً بتتبع التقدم.' : 'This item is not currently linked to progress tracking.'}
                            </Typography>
                        )}

                        {isVideo ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {isAr
                                    ? 'يتم تحديث التقدم تلقائياً أيضاً عند انتهاء مشاهدة الفيديو.'
                                    : 'Progress is also updated automatically when the video playback ends.'}
                            </Typography>
                        ) : null}
                    </DashboardPanelCard>
                </Box>
            </Box>
        </Box>
    );
}

export default LessonViewer;
